import { gql, useMutation } from '@apollo/client';
import type { DspBillPaymentVoucherType } from '../../queries/settlement/dspBillPayment';

export interface BillStatusChangeResponse {
    success: boolean;
    errorMessage?: string | null;
}

export const CONFIRM_DSP_BILL_INVOICE = gql`
    mutation ConfirmDspBillInvoice($billId: ID!, $remark: String) {
        confirmDspBillInvoice(billId: $billId, remark: $remark) {
            success
            errorMessage
        }
    }
`;

export const REVOKE_DSP_BILL_INVOICE_CONFIRM = gql`
    mutation RevokeDspBillInvoiceConfirm($billId: ID!, $remark: String) {
        revokeDspBillInvoiceConfirm(billId: $billId, remark: $remark) {
            success
            errorMessage
        }
    }
`;

export const REJECT_DSP_BILL_INVOICE = gql`
    mutation RejectDspBillInvoice($billId: ID!, $reason: String!) {
        rejectDspBillInvoice(billId: $billId, reason: $reason) {
            success
            errorMessage
        }
    }
`;

export const CONFIRM_DSP_BILL_PAYMENT = gql`
    mutation ConfirmDspBillPayment($billId: ID!, $remark: String) {
        confirmDspBillPayment(billId: $billId, remark: $remark) {
            success
            errorMessage
        }
    }
`;

export const ADD_DSP_BILL_PAYMENT_VOUCHER = gql`
    mutation AddDspBillPaymentVoucher(
        $billId: ID!
        $fileObjectKey: String!
        $fileName: String!
        $fileSize: Long!
        $contentType: String!
    ) {
        addDspBillPaymentVoucher(
            billId: $billId
            fileObjectKey: $fileObjectKey
            fileName: $fileName
            fileSize: $fileSize
            contentType: $contentType
        ) {
            id
            fileName
            fileSize
            contentType
            downloadUrl
            uploadedByName
            uploadedAt
        }
    }
`;

export const DELETE_DSP_BILL_PAYMENT_VOUCHER = gql`
    mutation DeleteDspBillPaymentVoucher($billId: ID!, $voucherId: ID!) {
        deleteDspBillPaymentVoucher(billId: $billId, voucherId: $voucherId) {
            success
            errorMessage
        }
    }
`;

export const useConfirmDspBillInvoice = () =>
    useMutation<{ confirmDspBillInvoice: BillStatusChangeResponse }, { billId: string; remark?: string }>(
        CONFIRM_DSP_BILL_INVOICE
    );

/**
 * 撤销确认只回退账单状态、不取消 Invoice 已提交；顺带刷新 Invoice 查询以同步最新状态。
 */
export const useRevokeDspBillInvoiceConfirm = () =>
    useMutation<{ revokeDspBillInvoiceConfirm: BillStatusChangeResponse }, { billId: string; remark?: string }>(
        REVOKE_DSP_BILL_INVOICE_CONFIRM,
        { refetchQueries: ['GetDspInvoiceByBillId'] }
    );

/**
 * 驳回会取消 Invoice 已提交，而总部侧 Invoice 的可见性以 submitted 为准 ——
 * 不顺带刷新这条查询，驳回后 Invoice 卡片会拿着缓存继续显示。
 */
export const useRejectDspBillInvoice = () =>
    useMutation<{ rejectDspBillInvoice: BillStatusChangeResponse }, { billId: string; reason: string }>(
        REJECT_DSP_BILL_INVOICE,
        { refetchQueries: ['GetDspInvoiceByBillId'] }
    );

export const useConfirmDspBillPayment = () =>
    useMutation<{ confirmDspBillPayment: BillStatusChangeResponse }, { billId: string; remark?: string }>(
        CONFIRM_DSP_BILL_PAYMENT
    );

export const useAddDspBillPaymentVoucher = () =>
    useMutation<
        { addDspBillPaymentVoucher: DspBillPaymentVoucherType },
        { billId: string; fileObjectKey: string; fileName: string; fileSize: number; contentType: string }
    >(ADD_DSP_BILL_PAYMENT_VOUCHER);

export const useDeleteDspBillPaymentVoucher = () =>
    useMutation<{ deleteDspBillPaymentVoucher: BillStatusChangeResponse }, { billId: string; voucherId: string }>(
        DELETE_DSP_BILL_PAYMENT_VOUCHER
    );
